import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Heading, TimeframeBar } from '../../components'
import { ChartEditorEnum, SpecificChartFunctionality, TIMEFRAME } from '../../constants/common'
import { useModelState } from '../../model-hooks/use-model-state'
import Chart from '../../models/chart'
import { ChartTypesSelect } from './chart-type-select'
import { CustomDateRange } from './custom-date-range'
import { DailyChangeDisplay } from './daily-change-display'
import { DrawButton } from './draw-button'
import { FullscreenButton } from './fullscreen-button'
import Search from './search'
import { ChartSettingsButton } from './settings-button'
import { useChartControls } from './use-chart-controls'
import { useFavoriteTimeframes } from './use-favorite-timeframes'
import { useTimeframeDateRangeGroups } from './use-timeframe-date-range-groups'

interface SettingsComponentProps {
  chartModel: Chart
  chartIndex: number
}

export function ChartControls({ chartIndex, chartModel: unwatchedChartModel }: SettingsComponentProps) {
  const navigate = useNavigate()
  const { favoriteTimeframes, toggleFavoriteTimeframe } = useFavoriteTimeframes()
  const chartLayout = unwatchedChartModel.chart_layout()
  const isPremium = window.FinvizSettings.hasUserPremium
  const isEditableChartOrFreeUser = !isPremium || chartLayout.editable
  const chartModel = useModelState(unwatchedChartModel, {
    watchProperties: ['ticker', 'quote'],
  })
  const {
    customDateRangeDialogState,
    handleDateRangeSelect,
    generateChartLayoutRouterPath,
    dateRangeGroups,
    timeFrameGroups,
  } = useTimeframeDateRangeGroups({ isPremium: isPremium, chartModel })
  const { isLoading } = useChartControls({
    chartModel,
    isInteractive: true,
  })

  const handleTickerSelect = React.useCallback(
    (ticker: string) => {
      const upperCaseTicker = ticker.toUpperCase()

      navigate(
        generateChartLayoutRouterPath({
          ticker: upperCaseTicker,
          timeframe: chartModel.timeframe,
          dateRange: chartModel.dateRange,
        })
      )
    },
    [chartModel, generateChartLayoutRouterPath, navigate]
  )

  switch (chartLayout.specificChartFunctionality) {
    case SpecificChartFunctionality.futuresPage:
    case SpecificChartFunctionality.forexPage:
    case SpecificChartFunctionality.cryptoPage:
      return (
        <div className="flex h-12 items-center space-x-2 rounded-t-md border border-b-0 border-gray-100 bg-white px-3.5 font-sans dark:border-gray-600 dark:bg-gray-800">
          <div className="flex items-center space-x-2 overflow-hidden">
            <Heading level={3} className="my-0 truncate uppercase text-gray-600 dark:text-gray-200">
              {chartModel.quote().name ?? chartModel.ticker}
            </Heading>
            {chartLayout.editors.includes(ChartEditorEnum.tools) && (
              <DrawButton
                active={chartLayout.editable}
                isPremium={isPremium}
                specificChartFunctionality={chartLayout.specificChartFunctionality}
              />
            )}
          </div>
          <div className="flex h-full flex-1 items-center justify-center space-x-2 overflow-hidden">
            <div>
              <ChartTypesSelect model={chartLayout} isPremium={isPremium} />
            </div>
            <FullscreenButton model={chartLayout} isPremium={isPremium}>
              Fullscreen
            </FullscreenButton>
            {chartLayout.editors.includes(ChartEditorEnum.settings) && isEditableChartOrFreeUser && (
              <ChartSettingsButton model={chartLayout} isPremium={isPremium}>
                Settings
              </ChartSettingsButton>
            )}
            <div className="grow" />
          </div>
          <DailyChangeDisplay chartIndex={0} />
        </div>
      )
    case SpecificChartFunctionality.chartPage:
      return (
        <div className="relative flex h-11 space-x-2 bg-white px-2 dark:bg-gray-800">
          {chartModel.chart_layout().canChangeTicker && (
            <Search
              ticker={chartModel.ticker}
              instrument={chartModel.instrument}
              onTickerSelected={handleTickerSelect}
              chartIndex={chartIndex}
            />
          )}

          <TimeframeBar
            size="medium"
            chartIndex={chartIndex}
            timeFrameGroups={timeFrameGroups}
            dateRangeGroups={dateRangeGroups}
            selectedTimeframe={chartModel.timeframe}
            onTimeframeSelect={() => {}}
            selectedDateRange={chartModel.dateRange}
            favoriteTimeframes={favoriteTimeframes}
            loadingTimeframe={isLoading ? chartModel.timeframe : undefined}
            onDateRangeSelect={handleDateRangeSelect}
            onFavoriteTimeframeToggle={({ value }: { value: TIMEFRAME }) => {
              toggleFavoriteTimeframe(value)
            }}
          />

          <DailyChangeDisplay chartIndex={chartIndex} />

          <CustomDateRange
            dialogState={customDateRangeDialogState}
            onSave={handleDateRangeSelect}
            chartModel={chartModel}
          />
        </div>
      )
    default:
      return null
  }
}
